<template>
    <div class="header_client_detail">
        <div class="title_row">
            <div class="identity">
                <p class="alias">{{ alias }}</p>
                <div class="meta_line is-flex is-align-items-center is-flex-wrap-wrap">
                    <span v-if="rut" class="rut">{{ rut }}</span>
                    <span v-if="counterpartType"
                        :class="['nobreak', 'pill_header', 'pill_sin_clasificar', counterpartTypeClass]">
                        {{ counterpartType }}
                    </span>
                    <span v-if="classificationLabel"
                        :class="['nobreak', 'pill_header', classificationClass]">
                        {{ classificationLabel }}
                    </span>
                </div>
            </div>

            <div class="right_cluster is-flex is-align-items-center">
                <div class="range_selector" v-click-outside="closeRangeDropdown">
                    <button class="range_trigger"
                        :class="{ 'is-open': isRangeOpen }"
                        :aria-expanded="isRangeOpen"
                        aria-haspopup="listbox"
                        @click="toggleRange">
                        <Calendar class="range_icon mr-2" />
                        <span>{{ rangeLabel }}</span>
                        <ArrowDown class="range_chevron ml-2" />
                    </button>
                    <ul v-if="isRangeOpen" class="range_menu" role="listbox">
                        <li v-for="opt in rangeOptions"
                            :key="opt.value"
                            class="range_option"
                            :class="{ 'is-selected': opt.value === rangePeriod }"
                            role="option"
                            :aria-selected="opt.value === rangePeriod"
                            @click="selectRange(opt.value)">
                            {{ opt.label }}
                        </li>
                    </ul>
                </div>

                <button class="export_btn ml-3 is-flex is-align-items-center"
                    disabled title="Próximamente">
                    <DownloadFile class="mr-2 export_icon" />
                    Exportar
                </button>

                <button class="close_btn ml-3" aria-label="Cerrar modal"
                    @click="$emit('close-modal')">
                    <Ecs class="ecs" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";

import clickOutside from "@/directives/clickOutside";
import Ecs from "@/components/svg/Ecs.vue";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import Calendar from "@/components/svg/Calendar.vue";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import { RANGE_OPTIONS } from "@/store/financialReports/clientDetail";

// Mapeo del campo raw del backend (counterpart.classify_by_counterpart) a
// label legible. El backend devuelve la familia ("Contraparte", "Mixta",
// "Documento", "Sin Clasificar"); el header pinta la forma extendida.
const CLASSIFICATION_LABELS = {
    Contraparte: "Clasificación por Contraparte",
    Mixta: "Clasificación Mixta",
    Documento: "Clasificación por Documento",
};

// Excepción al patrón de HeaderSalesAnalysis (presentational + payload): este
// header sí mapState del módulo clientDetail. La razón: counterpart/rut/pills
// se cargan async después de abrir el modal, y Buefy modal no propaga props
// reactivamente. El acople vale el ahorro de plumbing.
export default {
    name: "HeaderClientDetail",

    components: { Ecs, DownloadFile, Calendar, ArrowDown },

    // Directiva compartida bajo `src/directives/` — Buefy expone una similar
    // pero este header se monta vía $buefy.modal.open en document.body y no
    // siempre propaga eventos como un componente normal del subárbol.
    directives: { clickOutside },

    props: {
        // `payload` se acepta por contrato con ModalFullHeightRight pero
        // sólo `fallbackAlias` se consume — habilita pintar el alias antes
        // de que counterpart cargue async. El resto sale del store.
        payload: {
            type: Object,
            default: () => ({ fallbackAlias: "" }),
        },
    },

    data() {
        return {
            isRangeOpen: false,
        };
    },

    computed: {
        ...mapState("clientDetail", ["counterpart", "rangePeriod"]),
        ...mapGetters("clientDetail", ["rangeLabel"]),
        alias() {
            return this.counterpart?.alias || this.payload?.fallbackAlias || "";
        },
        rut() {
            return this.counterpart?.rut || "";
        },
        counterpartType() {
            return this.counterpart?.counterpart_type || "";
        },
        classifyByCounterpart() {
            return this.counterpart?.classify_by_counterpart || "";
        },
        rangeOptions() {
            return RANGE_OPTIONS;
        },
        // El backend marca clasificación en cuatro estados pero sólo tres
        // tienen render extendido (Contraparte/Mixta/Documento). "Sin
        // Clasificar" deja el pill vacío — no inflar el header con un chip
        // que no aporta información.
        classificationLabel() {
            if (!this.classifyByCounterpart) return "";
            return CLASSIFICATION_LABELS[this.classifyByCounterpart] ?? "";
        },
        // Compatibles 1:1 con las clases de ClassifyAccountAttributes.vue
        // — el pill heredan estilos compartidos (pill_header) + el modifier
        // específico que pinta emoji prefix (counterpart) o background+color
        // (classification). Cualquier cambio acá tiene que mirrors aquel.
        counterpartTypeClass() {
            if (!this.counterpartType) return "";
            return `pill_${this.counterpartType.toLowerCase().replace(/\s+/g, "_")}`;
        },
        classificationClass() {
            if (!this.classifyByCounterpart) return "";
            return `pill_${this.classifyByCounterpart.toLowerCase().replace(/\s+/g, "_")}`;
        },
    },

    methods: {
        toggleRange() {
            this.isRangeOpen = !this.isRangeOpen;
        },
        closeRangeDropdown() {
            this.isRangeOpen = false;
        },
        selectRange(value) {
            this.isRangeOpen = false;
            if (value === this.rangePeriod) return;
            this.$emit("update-range-period", value);
        },
    },
};
</script>

<style scoped lang="scss">
.header_client_detail {
    width: 100%;
    box-sizing: border-box;
}

.title_row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 24px;
    align-items: flex-start;
}

.identity {
    min-width: 0;
}

.alias {
    color: var(--report-text);
    font-size: 28px;
    font-weight: 700;
    line-height: 36px;
    margin: 0 0 8px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.meta_line {
    gap: 8px;
    color: var(--report-text-muted-strong);
}

.rut {
    font-size: 16px;
    font-weight: 500;
    color: var(--report-text-muted-strong);
    margin-right: 4px;
}

// Solo la base (sizing/border) vive scoped acá. Los modifiers cromáticos
// y de emoji están centralizados en `sass/elements/_pill_classifications`
// (importado vía style.scss). Reescalado respecto al modal legacy
// ClassifyAccountAttributes (font 20px → 14px): en este header el alias
// es muy grande (28px) y los pills deben pesar lo mismo que el rut (16px).
.pill_header {
    border-radius: 12px;
    background-color: var(--report-surface);
    /* #eaecf0: borde gris algo más cálido que --report-border; one-off sin token. */
    border: 1px solid #eaecf0;
    box-sizing: border-box;
    width: fit-content;
    padding: 2px 10px;
    font-size: 14px;
    margin-right: 3px;
}

.nobreak {
    white-space: nowrap;
}

.right_cluster {
    flex-shrink: 0;
}

.range_selector {
    position: relative;
}

.range_trigger {
    display: inline-flex;
    align-items: center;
    height: 40px;
    padding: 0 14px;
    background: var(--report-text);
    color: var(--report-bg);
    border: 1px solid var(--report-text);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 120ms ease;

    &:hover {
        background: var(--report-text-strong-2);
    }

    &.is-open {
        background: var(--report-text-strong-2);
    }
}

.range_icon {
    width: 16px;
    height: 16px;
    color: var(--report-bg);
}

.range_chevron {
    width: 12px;
    height: 12px;
    color: var(--report-bg);
}

.range_menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 200px;
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(16, 24, 40, 0.08);
    padding: 4px;
    margin: 0;
    list-style: none;
    z-index: 20;
}

.range_option {
    padding: 8px 12px;
    font-size: 14px;
    color: var(--report-text);
    border-radius: 6px;
    cursor: pointer;

    &:hover {
        background: var(--report-surface);
    }

    &.is-selected {
        background: var(--report-accent-bg);
        color: var(--report-accent-text);
        font-weight: 500;
    }
}

.export_btn {
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 8px;
    padding: 0 14px;
    height: 40px;
    font-size: 14px;
    font-weight: 500;
    color: var(--report-text-muted-strong);
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background: var(--report-surface);
    }

    &[disabled] {
        opacity: 0.55;
        cursor: not-allowed;
    }
}

.export_icon {
    width: 16px;
    height: 16px;
}

.close_btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid var(--report-border-soft);
    background: var(--report-bg);
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    box-sizing: border-box;
    overflow: hidden;
    padding-top: 5px;
    cursor: pointer;

    .ecs {
        width: 100%;
        position: relative;
        height: 20px;
        overflow: hidden;
        flex-shrink: 0;
    }
}
</style>
